import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { FaEdit, FaTrash, FaEye } from 'react-icons/fa';
import Swal from 'sweetalert2';
import RegionCreate from '../../create/js/regionCreate';
import DataTable from '../../../../components_shared/DataTable';
import TableToolbar from '../../../../components_shared/TableToolbar';
import { useApp } from '../../../../context/AppContext';
import { regionAPI } from '../../../../utils/api';
import PropTypes from 'prop-types';

function RegionTable({ onCreateRegion, reloadKey = 0 }) {
  const { permissions, roles: userRoles } = useApp();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  
  const isSuperAdmin = userRoles && (userRoles.includes('SUPER_ADMIN') || userRoles.includes('ROLE_SUPER_ADMIN'));
  const canCreate = permissions.includes('CREATE_REGION') || isSuperAdmin;
  const canEdit = permissions.includes('EDIT_REGION') || isSuperAdmin;
  const canDelete = permissions.includes('DELETE_REGION') || isSuperAdmin;

  const fetchRegions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await regionAPI.getAll();
      setData(res.data || []);
    } catch (err) {
      console.error('Failed to fetch regions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRegions();
  }, [fetchRegions, reloadKey]);

  const handleView = region => {
    setSelectedRegion(region);
    setShowViewModal(true);
  };

  const handleEdit = region => {
    setSelectedRegion(region);
    setShowEditModal(true);
  };

  const handleDelete = async (region) => {
    const result = await Swal.fire({
      title: 'Delete Region?',
      text: `Are you sure you want to delete ${region.name}? This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        await regionAPI.delete(region.id);
        await fetchRegions();
        Swal.fire('Deleted!', 'Region has been deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.message || 'Failed to delete region', 'error');
      }
    }
  };

  const columns = [
    { accessorKey: 'id', header: 'ID', size: 60 },
    { accessorKey: 'region_id', header: 'Code', size: 100 },
    { accessorKey: 'name', header: 'Region Name', size: 250 },
    { accessorKey: 'district_names', header: 'Districts', size: 300 },
    {
      id: 'actions',
      header: 'Actions',
      size: 150,
      cell: ({ row }) => (
        <div className="eduss-global-action-buttons">
          <button className="eduss-global-action-btn view" onClick={() => handleView(row.original)}><FaEye /></button>
          {canEdit && <button className="eduss-global-action-btn edit" onClick={() => handleEdit(row.original)}><FaEdit /></button>}
          {canDelete && <button className="eduss-global-action-btn delete" onClick={() => handleDelete(row.original)}><FaTrash /></button>}
        </div>
      ),
    },
  ];

  const filteredData = data.filter(r => 
    r.name?.toLowerCase().includes(filter.toLowerCase()) || 
    r.region_id?.toLowerCase().includes(filter.toLowerCase()) ||
    r.district_names?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="premium-card">
      <TableToolbar 
        title="Regions"
        subtitle="Manage primary geographic administrative regions"
        onSearch={setFilter}
        onAdd={canCreate ? onCreateRegion : null}
        addText="Add Region"
        showImportExport={true}
        onExport={() => regionAPI.exportCsv().then(res => {
          const url = window.URL.createObjectURL(new Blob([res.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'regions.csv');
          document.body.appendChild(link);
          link.click();
        })}
        onImport={async (file) => {
          const text = await file.text();
          await regionAPI.importCsv(text);
          fetchRegions();
          Swal.fire('Success', 'Regions imported successfully', 'success');
        }}
        onDownloadSample={() => {
          const fallback = 'id,name,code,district_ids,is_active\n,Central,CTR,1|2,true\n';
          const url = window.URL.createObjectURL(new Blob([fallback]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', 'regions_sample.csv');
          document.body.appendChild(link);
          link.click();
        }}
      />

      <div style={{ padding: '0 30px 30px' }}>
        <DataTable 
          data={filteredData} 
          columns={columns} 
          loading={loading}
        />
      </div>

      {showViewModal && selectedRegion && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setShowViewModal(false)}>
          <div className="eduss-global-modal eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Region Details</h3>
              <button onClick={() => setShowViewModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <table className="mini-info-table">
                <tbody>
                  <tr><th>ID</th><td>{selectedRegion.id}</td></tr>
                  <tr><th>Code</th><td>{selectedRegion.region_id}</td></tr>
                  <tr><th>Name</th><td>{selectedRegion.name}</td></tr>
                  <tr><th>Districts</th><td>{selectedRegion.district_names}</td></tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>,
        document.body
      )}

      {showEditModal && selectedRegion && createPortal(
        <div className="eduss-global-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="eduss-global-modal large eduss-admin-form-modal" onClick={e => e.stopPropagation()}>
            <div className="eduss-global-modal-header">
              <h3>Edit Region</h3>
              <button onClick={() => setShowEditModal(false)}>&times;</button>
            </div>
            <div className="eduss-global-modal-content">
              <RegionCreate
                region={selectedRegion}
                onClose={() => setShowEditModal(false)}
                onSubmit={() => { fetchRegions(); setShowEditModal(false); }}
              />
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

RegionTable.propTypes = {
  onCreateRegion: PropTypes.func,
  reloadKey: PropTypes.number
};

export default RegionTable;
